import { db } from '@/firebase'
import { selectPlates } from '@/components/fleetOrder'

/**
 * Fleet roster, read from the per-plate documents in `registerCar`.
 *
 * `registerCar` holds two shapes of document side by side:
 *   - one document per licence plate (id = the plate), carrying `size`,
 *     `order` and the cost/range attributes — written by
 *     `scripts/seed-car-info.js`;
 *   - the legacy `carSmall` / `carLarge` documents, each a `regNum` array.
 *
 * The per-plate documents are the source this module reads. `carSmall` /
 * `carLarge` are still present in Firestore and still correct, but nothing in
 * the app reads them any more — they are kept until every deployed build is on
 * this code, then removed. See scripts/README.md for the migration steps.
 *
 * The sessionStorage contract is deliberately unchanged: `carSmall` /
 * `carLarge` remain comma-joined plate strings, so all the existing
 * `sessionStorage.carSmall.split(",")` call sites keep working untouched.
 */

/**
 * Resolves to `{ small, large }`, each an array of plate strings.
 *
 * Served from sessionStorage when both keys are present, exactly as the old
 * per-document reads were. Pass `{ force: true }` to bypass that cache.
 * Rejects if the collection holds no per-plate documents, rather than quietly
 * returning empty lists that would render as a fleet of zero vehicles.
 */
export async function loadFleet({ force = false } = {}) {
  if (!force && sessionStorage.carSmall && sessionStorage.carLarge) {
    return {
      small: sessionStorage.carSmall.split(','),
      large: sessionStorage.carLarge.split(',')
    }
  }

  const snapshot = await db.collection('registerCar').get()

  const vehicles = []
  snapshot.forEach(doc => {
    const data = doc.data()
    // Skips the legacy carSmall / carLarge documents, which carry no `size`.
    if (!data || !data.size) return
    vehicles.push({
      regNum: data.regNum || doc.id,
      size: data.size,
      order: data.order
    })
  })

  if (!vehicles.length) {
    throw new Error('registerCar has no per-plate vehicle documents')
  }

  const small = selectPlates(vehicles, 'small')
  const large = selectPlates(vehicles, 'large')

  sessionStorage.carSmall = small.join(',')
  sessionStorage.carLarge = large.join(',')

  return { small, large }
}

export default { loadFleet }
